import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, ShieldCheck } from 'lucide-react';

// questions: data'dan gelen sorular (öğrenci, veli veya öğretmen)
// title: "Lise Öğrenci Formu" gibi bir başlık
// onSubmit: Anket bitince verileri ana sisteme/veritabanına gönderecek fonksiyon
const RibaSurvey = ({ questions, title, onSubmit, onBack }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // Hangi soruya ne cevap verildiğini tutar

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const progressPercentage = ((currentIndex + 1) / questions.length) * 100;

  // Şık bir otomatik ilerleme ve seçim kaydetme fonksiyonu
  const handleSelectOption = (optionType) => {
    setAnswers({ ...answers, [currentQuestion.id]: optionType });
    
    // Kullanıcı seçimi yaptıktan yarım saniye sonra otomatik diğer soruya geçsin (Harika UX)
    if (!isLastQuestion) {
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
      }, 400); // 400 milisaniye bekle ve geç
    }
  };

  const handleFinish = () => {
    // Tüm sorular cevaplandı mı kontrolü
    if (Object.keys(answers).length < questions.length) {
      alert("Lütfen anketi tamamlamadan önce tüm soruları yanıtladığınızdan emin olun.");
      return;
    }

    // Seçilen cevapları, arka plan kodları (M05, M24 vb.) ve hedefleriyle birlikte paketle
    const finalData = questions.map(q => {
      const selectedOption = answers[q.id]; // 'A' veya 'B'
      const optionData = selectedOption === 'A' ? q.optionA : q.optionB;
      
      return {
        questionId: q.id,
        selectedChoice: selectedOption, // A veya B
        target: optionData.target,      // "Atılganlık" vb.
        code: optionData.code            // "M05" vb.
      };
    });

    onSubmit(finalData); // Paketi ana sisteme gönder
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4 font-sans no-print">
      
      {/* Üst Kısım: Başlık ve İlerleme Çubuğu */}
      <div className="w-full max-w-3xl mb-8">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-800 font-bold text-sm flex items-center mb-6 transition">
          <ChevronLeft className="h-5 w-5 mr-1" /> Geri Çık
        </button>
        
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200 flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-black text-slate-800 mb-1">Rehberlik İhtiyacı Belirleme Anketi</h1>
            <p className="text-slate-500 font-medium text-sm">{title}</p>
          </div>
          <div className="h-12 w-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center font-black text-xl shadow-inner border border-blue-100">
            {currentIndex + 1}<span className="text-sm text-blue-300 font-medium">/{questions.length}</span>
          </div>
        </div>

        {/* İlerleme Çubuğu (Progress Bar) */}
        <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-600 transition-all duration-500 ease-out" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Soru Kartı */}
      <div className="w-full max-w-3xl bg-white p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-slate-100 relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
        
        <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold tracking-widest uppercase mb-8">
          Soru {currentQuestion.id}
        </div>

        <h2 className="text-xl md:text-2xl font-black text-slate-800 mb-8 leading-relaxed">
          Aşağıdaki iki rehberlik hizmetinden hangisine daha çok ihtiyaç olduğunu düşünüyorsun?
        </h2>

        {/* Seçenekler (A ve B) */}
        <div className="space-y-4">
          
          {/* A ŞIKKI */}
          <button 
            onClick={() => handleSelectOption('A')}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className={`w-full p-6 rounded-2xl border-2 text-left transition-all duration-300 flex items-center outline-none focus:outline-none group ${
              answers[currentQuestion.id] === 'A' 
              ? 'bg-white border-slate-300 shadow-sm ring-2 ring-slate-50' 
              : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <div className={`h-8 w-8 rounded-full flex items-center justify-center font-black mr-4 border-2 transition-colors shrink-0 ${
              answers[currentQuestion.id] === 'A' ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 text-slate-400 group-hover:border-slate-400 group-hover:text-slate-600'
            }`}>
              A
            </div>
            <span className={`text-lg font-medium leading-relaxed transition-colors ${answers[currentQuestion.id] === 'A' ? 'text-slate-900 font-bold' : 'text-slate-700'}`}>
              {currentQuestion.optionA.text}
            </span>
          </button>

          {/* B ŞIKKI */}
          <button 
            onClick={() => handleSelectOption('B')}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className={`w-full p-6 rounded-2xl border-2 text-left transition-all duration-300 flex items-center outline-none focus:outline-none group ${
              answers[currentQuestion.id] === 'B' 
              ? 'bg-white border-slate-300 shadow-sm ring-2 ring-slate-50' 
              : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <div className={`h-8 w-8 rounded-full flex items-center justify-center font-black mr-4 border-2 transition-colors shrink-0 ${
              answers[currentQuestion.id] === 'B' ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 text-slate-400 group-hover:border-slate-400 group-hover:text-slate-600'
            }`}>
              B
            </div>
            <span className={`text-lg font-medium leading-relaxed transition-colors ${answers[currentQuestion.id] === 'B' ? 'text-slate-900 font-bold' : 'text-slate-700'}`}>
              {currentQuestion.optionB.text}
            </span>
          </button>

        </div>

        {/* Alt Navigasyon Butonları */}
        <div className="flex justify-between items-center mt-12 pt-6 border-t border-slate-100">
          <button 
            onClick={() => setCurrentIndex(prev => prev - 1)} 
            disabled={currentIndex === 0}
            className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition disabled:opacity-30 disabled:cursor-not-allowed flex items-center"
          >
            <ChevronLeft className="h-5 w-5 mr-1"/> Önceki
          </button>
          
          {isLastQuestion ? (
            <button 
              onClick={handleFinish}
              disabled={!answers[currentQuestion.id]}
              className="px-8 py-4 bg-green-600 hover:bg-green-500 text-white rounded-xl font-black transition shadow-lg shadow-green-600/30 flex items-center disabled:opacity-50"
            >
              <CheckCircle className="h-5 w-5 mr-2"/> Anketi Tamamla
            </button>
          ) : (
            <button 
              onClick={() => setCurrentIndex(prev => prev + 1)}
              disabled={!answers[currentQuestion.id]}
              className="px-8 py-3 bg-slate-900 hover:bg-blue-600 text-white rounded-xl font-bold transition shadow-lg flex items-center disabled:opacity-50"
            >
              Sonraki <ChevronRight className="h-5 w-5 ml-1"/>
            </button>
          )}
        </div>

      </div>

      <div className="mt-8 flex items-center text-slate-400 text-xs font-bold uppercase tracking-widest">
        <ShieldCheck className="h-4 w-4 mr-1"/> Sadece tek bir seçeneği işaretleyebilirsiniz.
      </div>

    </div>
  );
};

export default RibaSurvey;